import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import MessageInput from './MessageInput';
import { cn } from '../../../utils/cn';

const ChatArea = ({ conversation, onSendMessage }) => {
  const messagesEndRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [conversation?.messages]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday?.setDate(yesterday?.getDate() - 1);

    if (date?.toDateString() === today?.toDateString()) {
      return 'Today';
    } else if (date?.toDateString() === yesterday?.toDateString()) {
      return 'Yesterday';
    } else {
      return date?.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date?.getFullYear() !== today?.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const shouldShowDateSeparator = (currentMessage, previousMessage) => {
    if (!previousMessage) return true;
    
    const currentDate = new Date(currentMessage.timestamp)?.toDateString();
    const previousDate = new Date(previousMessage.timestamp)?.toDateString();
    
    return currentDate !== previousDate;
  };

  const getMessageStatusIcon = (status) => {
    switch (status) {
      case 'sending':
        return <Icon name="Clock" size={14} className="text-muted-foreground" />;
      case 'delivered':
        return <Icon name="Check" size={14} className="text-muted-foreground" />;
      case 'read':
        return <Icon name="CheckCheck" size={14} className="text-primary" />;
      default:
        return null;
    }
  };

  const handleSendMessage = (content, attachments = []) => {
    onSendMessage(content, attachments);
    
    // Simulate vendor typing response (for demo purposes)
    if (conversation?.vendorName) {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        // Could add auto-response here for demo
      }, 2000);
    }
  };

  if (!conversation) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Icon name="MessageCircle" size={48} className="mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium mb-2">No conversation selected</h3>
          <p className="text-sm">Choose a conversation from the sidebar to start messaging</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {conversation?.messages?.map((message, index) => {
          const previousMessage = index > 0 ? conversation?.messages?.[index - 1] : null;
          const showDateSeparator = shouldShowDateSeparator(message, previousMessage);
          const isCurrentUser = message?.senderType === 'buyer';

          return (
            <div key={message?.id}>
              {/* Date Separator */}
              {showDateSeparator && (
                <div className="flex items-center justify-center my-6">
                  <div className="bg-muted px-3 py-1 rounded-full text-xs text-muted-foreground">
                    {formatDate(message?.timestamp)}
                  </div>
                </div>
              )}
              {/* Message */}
              <div className={cn(
                "flex items-end space-x-2 max-w-[70%]",
                isCurrentUser ? "ml-auto flex-row-reverse space-x-reverse" : ""
              )}>
                {/* Avatar */}
                {!isCurrentUser && (
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex-shrink-0">
                    <Image
                      src={conversation?.vendorImage}
                      alt={`${message?.senderName} profile picture`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Message Bubble */}
                <div className={cn(
                  "rounded-2xl px-4 py-2 max-w-full",
                  isCurrentUser 
                    ? "bg-primary text-primary-foreground rounded-br-sm" 
                    : "bg-muted text-foreground rounded-bl-sm"
                )}>
                  {/* Message Content */}
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {message?.content}
                  </p>

                  {/* Attachments */}
                  {message?.attachments && message?.attachments?.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {message?.attachments?.map((attachment, attachIndex) => (
                        <div key={attachIndex} className="flex items-center space-x-2 p-2 bg-black/10 rounded-lg">
                          <Icon name="Paperclip" size={16} />
                          <span className="text-xs">{attachment?.name}</span>
                          <Button size="xs" variant="ghost">
                            <Icon name="Download" size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Time and Status */}
                  <div className={cn(
                    "flex items-center justify-end space-x-1 mt-1",
                    isCurrentUser ? "text-primary-foreground/70" : "text-muted-foreground"
                  )}>
                    <span className="text-xs">
                      {formatTime(message?.timestamp)}
                    </span>
                    {isCurrentUser && getMessageStatusIcon(message?.status)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Typing Indicator */}
        {isTyping && (
          <div className="flex items-end space-x-2 max-w-[70%]">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={conversation?.vendorImage}
                alt={`${conversation?.vendorName} profile picture`}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="bg-muted text-foreground rounded-2xl rounded-bl-sm px-4 py-3">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
      {/* Quick Actions */}
      <div className="px-4 py-2 border-t border-border bg-muted/20">
        <div className="flex space-x-2">
          <Button size="sm" variant="outline" className="text-xs">
            <Icon name="Calendar" size={14} className="mr-1" />
            Schedule Meeting
          </Button>
          <Button size="sm" variant="outline" className="text-xs">
            <Icon name="DollarSign" size={14} className="mr-1" />
            Send Quote Request
          </Button>
          <Button size="sm" variant="outline" className="text-xs">
            <Icon name="FileText" size={14} className="mr-1" />
            Share Document
          </Button>
        </div>
      </div>
      {/* Message Input */}
      <MessageInput onSendMessage={handleSendMessage} />
    </div>
  );
};

export default ChatArea;